// src/pages/admin/UserManagement.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FiEdit2, FiTrash2, FiPlus, FiUser, FiTruck,FiSlash } from "react-icons/fi";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals Toggle
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  // Form States
  const [selectedUser, setSelectedUser] = useState(null);
  const [profileForm, setProfileForm] = useState({ full_name: "", email: "", role: "user" });
  const [newUserForm, setNewUserForm] = useState({ full_name: "", email: "", role: "user" });

  const [providerForm, setProviderForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    service_type: "",
    user_id: "" // Link to profiles
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [profilesRes, providersRes] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("providers").select("*").order("created_at", { ascending: false })
    ]);

    if (profilesRes.error) console.error(profilesRes.error);
    if (providersRes.error) console.error(providersRes.error);

    setUsers(profilesRes.data || []);
    setProviders(providersRes.data || []);
    setLoading(false);
  };

  // --- Profile Actions ---
  const handleEditProfile = (user) => {
    setSelectedUser(user);
    setProfileForm({ full_name: user.full_name, email: user.email, role: user.role });
    setShowProfileModal(true);
  };

  const updateProfile = async () => {
    const { error } = await supabase
      .from("profiles")
      .update(profileForm)
      .eq("id", selectedUser.id);

    if (error) alert(error.message);
    else {
      setShowProfileModal(false);
      fetchData();
    }
  };

  const handleCreateUser = async () => {
    if (!newUserForm.email) {
      alert("Please provide an email address.");
      return;
    }
    try {
      const payload = {
        id: crypto.randomUUID(),
        full_name: newUserForm.full_name,
        email: newUserForm.email,
        role: newUserForm.role || "user",
      };
      const { error } = await supabase.from("profiles").insert([payload]);
      if (error) throw error;
      setShowAddUserModal(false);
      setNewUserForm({ full_name: "", email: "", role: "user" });
      fetchData();
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  // --- Provider Actions ---
  const handleAddProvider = async () => {
  try {
    const payload = {
      full_name: providerForm.full_name,
      email: providerForm.email,
      phone: providerForm.phone,
      service_type: providerForm.service_type,
    };

    console.log("Sending:", payload);

    const { data, error } =
      await supabase.functions.invoke(
        "create-provider",
        {
          body: payload,
        }
      );

    if (error) throw error;


    setShowProviderModal(false);

    setProviderForm({
      full_name: "",
      email: "",
      phone: "",
      service_type: "",
      user_id: "",
    });

    fetchData();
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

  const disableProvider = async (providerId) => {
  if (
    !window.confirm(
      "Remove provider from all assigned requests?"
    )
  ) {
    return;
  }

  try {
    const { error } = await supabase
      .from("service_requests")
      .update({
        provider_id: null,
      })
      .eq("provider_id", providerId);

    if (error) throw error;

    alert("Provider unassigned from all requests.");
    fetchData();
  } catch (err) {
    alert(err.message);
  }
};


  const deleteUser = async (id, table) => {
    if (window.confirm(`Are you sure you want to delete this from ${table}?`)) {
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error) alert(error.message);
      else fetchData();
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen">Loading Platform Data...</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 md:px-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Platform Management</h1>
        <p className="text-gray-600">Manage your Users and Service Providers in one place</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* LEFT COLUMN: PROFILES */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-center justify-between font-bold text-gray-700">
            <div className="flex items-center gap-2">
              <FiUser className="text-blue-500" /> System Users (Profiles)
            </div>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-700 transition shadow-sm"
            >
              <FiPlus /> Add New User
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 text-sm">
                    <td className="px-4 py-3">
                      <div className="font-medium">{user.full_name || "N/A"}</div>
                      <div className="text-xs text-gray-400">{user.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => handleEditProfile(user)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><FiEdit2 /></button>
                      <button onClick={() => deleteUser(user.id, 'profiles')} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><FiTrash2 /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* RIGHT COLUMN: PROVIDERS */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-center justify-between font-bold text-gray-700">
            <div className="flex items-center gap-2">
              <FiTruck className="text-green-500" /> Service Providers
            </div>
            <button
              onClick={() => setShowProviderModal(true)}
              className="flex items-center gap-1.5 bg-green-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-green-700 transition shadow-sm"
            >
              <FiPlus /> Add New Provider
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {providers.map((provider) => (
                  <tr key={provider.id} className="hover:bg-gray-50 text-sm">
                    <td className="px-4 py-3">
                      <div className="font-medium">{provider.full_name}</div>
                      <div className="text-xs text-gray-400">{provider.phone || provider.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs capitalize">
                        {provider.service_type?.replace('-', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">

  <button
    onClick={() => disableProvider(provider.id)}
    className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
    title="Disable Provider Login"
  >
    <FiSlash />
  </button>

  <button
    onClick={() => deleteUser(provider.id, "providers")}
    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
    title="Delete Provider"
  >
    <FiTrash2 />
  </button>

</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {providers.length === 0 && <p className="p-10 text-center text-gray-400">No providers registered yet.</p>}
          </div>
        </motion.div>
      </div>

      <div className="mt-8">
        <Link to="/dashboard" className="text-blue-600 hover:underline">← Back to Dashboard</Link>
      </div>

      {/* --- MODAL: EDIT PROFILE --- */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit User Profile</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input 
                  className="w-full border rounded-lg p-2"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select 
                  className="w-full border rounded-lg p-2"
                  value={profileForm.role}
                  onChange={(e) => setProfileForm({...profileForm, role: e.target.value})}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="provider">Provider</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowProfileModal(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                <button onClick={updateProfile} className="bg-blue-600 text-white px-6 py-2 rounded-lg">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: ADD PROVIDER --- */}
      {showProviderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add New Provider</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input 
                  className="w-full border rounded-lg p-2"
                  placeholder="Business or Person Name"
                  onChange={(e) => setProviderForm({...providerForm, full_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input 
                  type="email"
                  className="w-full border rounded-lg p-2"
                  placeholder="provider@example.com"
                  onChange={(e) => setProviderForm({...providerForm, email: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input 
                  className="w-full border rounded-lg p-2"
                  placeholder="+1 234 567 890"
                  onChange={(e) => setProviderForm({...providerForm, phone: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Category</label>
                <select 
                  className="w-full border rounded-lg p-2"
                  onChange={(e) => setProviderForm({...providerForm, service_type: e.target.value})}
                >
                  <option value="">Select a service</option>
                  <option value="property-care">Property Care</option>
                  <option value="parental-care">Parental Care</option>
                  <option value="travel-services">Travel Services</option>
                  <option value="celebration-services">Celebration Services</option>
                  <option value="financial-services">Financial Services</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-blue-600">Link to Existing User Account (Optional)</label>
                <select 
                  className="w-full border border-blue-200 rounded-lg p-2 bg-blue-50"
                  onChange={(e) => setProviderForm({...providerForm, user_id: e.target.value})}
                >
                  <option value="">No link (Independent Provider)</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowProviderModal(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                <button onClick={handleAddProvider} className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">Register Provider</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* --- MODAL: ADD NEW USER --- */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add New System User</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input 
                  className="w-full border rounded-lg p-2 text-sm"
                  placeholder="John Doe"
                  value={newUserForm.full_name}
                  onChange={(e) => setNewUserForm({...newUserForm, full_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input 
                  type="email"
                  required
                  className="w-full border rounded-lg p-2 text-sm"
                  placeholder="user@example.com"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select 
                  className="w-full border rounded-lg p-2 text-sm"
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({...newUserForm, role: e.target.value})}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="provider">Provider</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowAddUserModal(false)} className="px-4 py-2 text-gray-600 text-sm font-medium">Cancel</button>
                <button onClick={handleCreateUser} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium shadow transition">Create User</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;